// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unused-vars, prefer-const, react-hooks/exhaustive-deps, react-hooks/immutability */

import _ from "lodash";
import moment from "moment";
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon, Loader, Segment, Table } from "semantic-ui-react";
import { useUserAuth } from "../../../hooks/useUserAuth";
import "./table.css";
import { Action, AnyFilter, BooleanFilter, DateFilter, StringFilter } from "./types";
import Tooltip from "rc-tooltip";
import { iconColorMap } from "../../../utilities/search";
import Pagination from "../Pagination";

type RenderOptions = { breaks?: boolean; title?: string };
type TextToRender = string | string[];
type IconsToRender = { icon: string; color: string; title: string; enabled: boolean }[];
type Renderable = TextToRender | IconsToRender;
type RenderableWithOptions = { render: Renderable; options?: RenderOptions };

interface TableProps<T extends {}> {
	data: T[];
	// setData: Dispatch<React.SetStateAction<T[]>>;
	headers: {
		[key in keyof T]?: string;
	};
	onRenderDataColumn?: {
		[key in keyof T]?: (col: T[key]) => Renderable | RenderableWithOptions | undefined;
	};
	onSortColumn?: {
		[key in keyof T]?: ((a: T[key], b: T[key]) => number) | boolean;
	};
	columnFilters?: {
		[key in keyof T]?: AnyFilter;
	};
	links?: {
		[key in keyof T]?: (row: T[key] extends any[] ? T[key][0] : T) => string;
	};
	actions?: Action<T>[];
	level: "flight" | "farm" | "field" | "trial" | "protocol";
	loading: boolean;
	searchTerm?: { term: string; cols: (keyof T)[] };
	initialSortColumn?: keyof T;
	rowKey?: keyof T;
}

const isWithOptions = (render: Renderable | RenderableWithOptions | undefined): render is RenderableWithOptions => {
	return (render as RenderableWithOptions)?.render !== undefined;
};

const MarkedText = ({ text, pattern }: { text: string; pattern: RegExp }) => {
	const parts = useMemo(() => String(text).split(pattern), [text, pattern]);
	return (
		<>
			{_.map(parts, (p, i) =>
				pattern.test(p) ? <mark key={`${p}-${i}`}>{p}</mark> : <span key={`${p}-${i}`}>{p}</span>,
			)}
		</>
	);
};

const ResultsTable = <T extends {}>({
	data,
	// setData,
	headers,
	onRenderDataColumn,
	onSortColumn,
	columnFilters,
	links,
	actions,
	level,
	loading = false,
	searchTerm,
	initialSortColumn,
	rowKey,
}: TableProps<T>): React.ReactElement => {
	const [sortDirection, setSortDirection] = useState<"descending" | "ascending">("descending");
	const [sortedColumn, setSortedColumn] = useState<keyof T | "">("");
	const [queriedData, setQueriedData] = useState<T[]>(data);
	const [sortedData, setSortedData] = useState<T[]>(data);
	const [filteredData, setFilteredData] = useState<T[]>(data);
	const [pageSize, setPageSize] = useState<10 | 25 | 50 | 100>(25);
	const [pageNumber, setPageNumber] = useState(1);
	const [initialRender, setInitialRender] = useState(true);

	const userAuth = useUserAuth();

	const maxPage = useMemo(() => Math.ceil(filteredData.length / pageSize), [filteredData, pageSize]);
	const paginatedData = useMemo(
		() => filteredData.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
		[filteredData, pageSize, pageNumber],
	);

	useEffect(() => {
		if (searchTerm) {
			const { term, cols } = searchTerm;
			if (term) {
				setQueriedData(() => {
					const re = RegExp(_.escapeRegExp(term), "i");
					let queriedData: T[] = [];
					let remainingData = [...data];

					// keep track of leftover data so that we can order data by column importance
					for (let keyString of cols) {
						const keyStack = (keyString as string).split(".");
						const col = keyStack[0];
						// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
						if (Array.isArray(data[0][col]) && keyStack[1]) {
							queriedData = queriedData.concat(
								_.filter(
									remainingData,
									// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
									(r) => _.filter(r[col], (point) => re.test(point[keyStack[1]] as string)).length > 0,
								),
							);
							remainingData = _.filter(
								remainingData,
								// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
								(r) => _.filter(r[col], (point) => re.test(point[keyStack[1]] as string)).length <= 0,
							);
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
						} else if (Array.isArray(data[0][col]) && !keyStack[1]) {
							queriedData = queriedData.concat(
								// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
								_.filter(remainingData, (r) => _.filter(r[col], (point) => re.test(point as string)).length > 0),
							);
							remainingData = _.filter(
								remainingData,
								// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
								(r) => _.filter(r[col], (point) => re.test(point as string)).length <= 0,
							);
						} else {
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
							queriedData = queriedData.concat(_.filter(remainingData, (r) => re.test(r[col] as string)));
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
							remainingData = _.filter(remainingData, (r) => !re.test(r[col] as string));
						}
					}

					return queriedData;
				});
			} else {
				setQueriedData(data);
			}
		} else {
			setQueriedData(data);
		}
	}, [data, searchTerm?.term]);

	useEffect(() => {
		if (sortedColumn !== "" && onSortColumn !== undefined) {
			const compareFn = onSortColumn[sortedColumn];

			if (typeof compareFn !== "function") {
				setSortedData(() => {
					const sortableRows = _.filter(queriedData, (r) => r[sortedColumn] !== null);
					const nullRows = _.filter(queriedData, (r) => r[sortedColumn] === null);
					let sorted = _.sortBy(sortableRows, (r) => String(r[sortedColumn]).toUpperCase());

					if (sortDirection === "ascending") {
						sorted.reverse();
					}
					sorted = sorted.concat(...nullRows);
					return sorted;
				});
			} else {
				setSortedData(() => {
					const sortableRows = _.filter(queriedData, (r) => r[sortedColumn] !== null);
					const nullRows = _.filter(queriedData, (r) => r[sortedColumn] === null);
					let sorted = sortableRows.sort((a, b) => {
						return compareFn(a[sortedColumn], b[sortedColumn]);
					});

					if (sortDirection === "ascending") {
						sorted.reverse();
					}
					sorted = sorted.concat(...nullRows);
					return sorted;
				});
			}

			setInitialRender(false);
		} else if (queriedData.length > 0 && initialRender && initialSortColumn && onSortColumn !== undefined) {
			let initialCompareFn = onSortColumn[initialSortColumn];
			let sorted = queriedData;

			if (typeof initialCompareFn !== "function") {
				sorted = _.sortBy(queriedData, (r) => String(r[initialSortColumn]).toUpperCase());
			} else {
				sorted = queriedData.sort((a, b) => {
					return initialCompareFn(a[initialSortColumn], b[initialSortColumn]);
				});
			}

			setInitialRender(false);
			setSortedData(sorted);
		} else {
			setSortedData(queriedData);
		}
	}, [sortedColumn, sortDirection, queriedData]);

	useEffect(() => {
		if (columnFilters) {
			filterColumns();
		} else {
			// case when no "live" filters
			setFilteredData(sortedData);
		}
	}, [sortedData, columnFilters]);

	const filterColumns = useCallback(() => {
		if (!columnFilters) {
			setFilteredData(sortedData);
			return;
		}
		const columnsToFilter = Object.keys(columnFilters);
		if (columnsToFilter.length < 1) {
			setFilteredData(sortedData);
			return;
		}
		const filtered = _.filter(sortedData, (row) => {
			const shouldShow = _.reduce(
				Object.entries<AnyFilter | undefined>(columnFilters),
				(acc, [keyString, filter]) => {
					const keyStack = keyString.split(".");
					const col = keyStack[0];

					// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
					if ((row[col] === null || row[col] === undefined) && filter!.showNulls) {
						return acc;
					}

					switch (filter!.type) {
						case "string": {
							const names = (filter as StringFilter).names;
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
							let point = row[col] ? row[col] : null;
							if (Array.isArray(point)) {
								if (keyStack[1]) {
									point = point.map((p) => p[keyStack[1]]);
								}
								acc = acc && ((point.length === 0 && names.includes(null)) || names.some((r) => point.includes(r)));
							} else {
								acc = acc && names.includes(point);
							}
							break;
						}
						case "boolean": {
							const values = (filter as BooleanFilter).values;
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
							acc = acc && values.includes(row[col]);
							break;
						}
						case "date": {
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
							if (row[col] === null) return false;
							const startDate = (filter as DateFilter).startDate;
							const endDate = (filter as DateFilter).endDate;
							// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
							const point = moment(row[col].split(" ")[0]);
							acc = acc && point.isBetween(startDate, endDate, undefined, "[]");
							break;
						}
					}
					return acc;
				},
				true,
			);
			return shouldShow;
		});

		setFilteredData(filtered);
	}, [sortedData, columnFilters]);

	const changeSort = useCallback(
		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
		(prop) => {
			if (sortedColumn === prop) {
				setSortDirection((prev) => {
					if (prev === "descending") return "ascending";
					else return "descending";
				});
			} else {
				setSortedColumn(prop);
				setSortDirection("descending");
			}
		},
		[sortedColumn, sortDirection],
	);

	return (
		<>
			<Segment className="tableContainer" basic>
				{loading ? (
					<div style={{ height: "100%", width: "100%", display: "flex" }}>
						{" "}
						<Loader active />{" "}
					</div>
				) : (
					<Table className="resultsTable">
						<Table.Header>
							<Table.Row>
								{_.map(Object.entries<string | undefined>(headers), ([prop, display]) =>
									// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
									onSortColumn && onSortColumn[prop] ? (
										<Table.HeaderCell
											key={prop}
											className="sortable"
											sorted={sortedColumn === prop ? sortDirection : undefined}
											onClick={() => {
												changeSort(prop);
											}}
										>
											<span>{display}</span>
											{/* @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types) */}
											{columnFilters && columnFilters[prop]?.applied && (
												<Icon name="filter" className="headerFilterIcon" size="small" />
											)}
										</Table.HeaderCell>
									) : (
										<Table.HeaderCell key={prop}>
											<span>{display}</span>
											{/* @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types) */}
											{columnFilters && columnFilters[prop]?.applied && (
												<Icon name="filter" className="headerFilterIcon" size="small" />
											)}
										</Table.HeaderCell>
									),
								)}
								<Table.HeaderCell>Actions</Table.HeaderCell>
							</Table.Row>
						</Table.Header>
						<Table.Body>
							{_.map(paginatedData, (row) => {
								const re = searchTerm?.term ? new RegExp(`(${_.escapeRegExp(searchTerm.term)})`, "gi") : /nothing^/;
								// map each row
								return (
									<Table.Row
										key={rowKey ? row[rowKey] : (row as any)[`${level}Id`]}
										style={{
											backgroundColor:
												// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
												row["error"] && row["farmClientId"] === userAuth.currentClientId
													? "var(--terracotta-200)"
													: "revert-layer",
										}}
									>
										{_.map(Object.keys(headers), (th, i) => (
											<Table.Cell key={th + i}>
												{/*  @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types) */}
												{(() => {
													// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
													const col = row[th];
													let options: RenderOptions = { breaks: false };
													let textToRender: TextToRender | null = null;
													let iconsToRender: IconsToRender | null = null;

													// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
													if (onRenderDataColumn && onRenderDataColumn[th]) {
														let renderedCol: Renderable | RenderableWithOptions | undefined =
															// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
															onRenderDataColumn[th](col);
														if (isWithOptions(renderedCol)) {
															options = renderedCol.options ?? options;
															renderedCol = renderedCol.render;
														}
														if (Array.isArray(renderedCol) && typeof renderedCol[0] !== "string") {
															iconsToRender = renderedCol as IconsToRender;
															textToRender = null;
														} else {
															textToRender = (renderedCol as TextToRender | undefined) ?? "-";
														}
													} else {
														textToRender = col ?? "-";
													}
													// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
													const hasLink = !!links && !!links[th];
													// if (Array.isArray(textToRender)) {
													// 	textToRender = textToRender;
													// }

													if (hasLink) {
														if (textToRender) {
															if (Array.isArray(textToRender)) {
																return _.map(textToRender, (ttr, i) => (
																	<React.Fragment key={ttr}>
																		<Link
																			// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
																			to={links[th](col[i])}
																			target="_blank"
																			style={{ whiteSpace: "nowrap" }}
																			title={ttr.length > 20 ? ttr : undefined}
																		>
																			<MarkedText
																				text={ttr.length > 20 ? ttr.slice(0, 17) + "..." : ttr}
																				pattern={re}
																			/>
																		</Link>
																		{i - 1 < textToRender.length && <br />}
																	</React.Fragment>
																));
															} else {
																return (
																	<Link
																		// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
																		to={links[th](row)}
																		target="_blank"
																		title={options.title}
																		// dangerouslySetInnerHTML={{ __html: textToRender.replaceAll("_", "_<wbr/>") }}
																	>
																		<span style={{ whiteSpace: "pre" }}>
																			<MarkedText text={textToRender} pattern={re} />
																		</span>
																	</Link>
																);
															}
														} else {
															return <></>;
														}
													} else {
														if (textToRender !== null) {
															if (Array.isArray(textToRender)) {
																if (options.breaks) {
																	return _.map(textToRender, (ttr, i) => <p key={ttr}>{ttr}</p>);
																} else {
																	if (textToRender.length > 5) {
																		return (
																			<span>
																				<MarkedText text={textToRender.sort().slice(0, 5).join(", ")} pattern={re} />{" "}
																				<span title={textToRender.sort().join(", ")}>...</span>
																			</span>
																		);
																	} else {
																		return <MarkedText text={textToRender.sort().join(", ")} pattern={re} />;
																	}
																}
															}
															return (
																<span title={options.title}>
																	<MarkedText text={textToRender} pattern={re} />
																</span>
															);
														} else if (iconsToRender !== null) {
															return iconsToRender.reduce<ReactNode[]>((acc, itr) => {
																if (!itr.enabled) return acc;
																acc.push(
																	<Tooltip
																		key={itr.title}
																		placement="bottom"
																		mouseEnterDelay={0}
																		mouseLeaveDelay={0}
																		trigger="hover"
																		overlay={itr.title}
																		motion={{ motionName: "rc-tooltip-zoom" }}
																	>
																		<Icon
																			className={itr.icon}
																			// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
																			style={{ color: iconColorMap[itr.color] ?? itr.color }}
																		/>
																	</Tooltip>,
																);
																return acc;
															}, []);
														}
													}
												})()}
											</Table.Cell>
										))}
										<Table.Cell>
											{_.map(actions, (action) =>
												!userAuth.isApAdmin && !action.hasArea(row) ? (
													"icon" in action ? (
														<Icon key={action.title} className={action.icon} disabled />
													) : (
														action.component(row, false)
													)
												) : "to" in action ? (
													<Tooltip
														key={action.title}
														placement="bottom"
														mouseEnterDelay={0}
														mouseLeaveDelay={0}
														trigger="hover"
														overlay={action.title}
														motion={{ motionName: "rc-tooltip-zoom" }}
													>
														<Link to={action.to(row)} target="_blank">
															<Icon className={action.icon} />
														</Link>
													</Tooltip>
												) : "onClick" in action ? (
													<Tooltip
														key={action.title}
														placement="bottom"
														mouseEnterDelay={0}
														mouseLeaveDelay={0}
														trigger="hover"
														overlay={action.title}
														motion={{ motionName: "rc-tooltip-zoom" }}
													>
														<a style={{ cursor: "pointer" }} onClick={() => action.onClick(row)}>
															<Icon className={action.icon} />
														</a>
													</Tooltip>
												) : "component" in action ? (
													action.component(row, true)
												) : (
													<></>
												),
											)}
										</Table.Cell>
									</Table.Row>
								);
							})}
						</Table.Body>
					</Table>
				)}
			</Segment>
			<Pagination
				displayItemCount={true}
				itemCount={filteredData.length}
				itemName={level}
				pageNumber={pageNumber}
				setPageNumber={setPageNumber}
				maxPage={maxPage}
				pageSize={pageSize}
				// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
				setPageSize={setPageSize}
			/>
		</>
	);
};

export default ResultsTable;
